import { Box, Stack } from '@mui/material';
import { Children, useRef, useState } from 'react';

/** Width of each card, leaving the next one peeking to signal there's more. */
const ITEM_WIDTH = '85%';
/** A lone card has no next card to hint at, so the peek would just be dead space. */
const SINGLE_ITEM_WIDTH = '100%';

/**
 * Swipeable row of cards with a dot pager — the phone layout for the hybrid cards. Scroll
 * snapping does the paging; the dots only report and jump, so both stay in agreement.
 */
function CardCarousel({ children }: { children: React.ReactNode }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(0);
    const items = Children.toArray(children);

    const cardElements = () => Array.from(scrollRef.current?.children || []) as HTMLElement[];

    const handleScroll = () => {
        const container = scrollRef.current;
        if (!container) return;
        const centre = container.scrollLeft + container.clientWidth / 2;
        const index = cardElements().findIndex(
            (card) => card.offsetLeft <= centre && card.offsetLeft + card.offsetWidth > centre
        );
        if (index >= 0 && index !== active) setActive(index);
    };

    const scrollTo = (index: number) => {
        // `block: 'nearest'` keeps the jump horizontal instead of yanking the page down.
        cardElements()[index]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    };

    return (
        <Stack spacing={1}>
            <Box
                ref={scrollRef}
                onScroll={handleScroll}
                sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {items.map((item, index) => (
                    <Box
                        // biome-ignore lint/suspicious/noArrayIndexKey: positional slides, and the child carries its own key
                        key={index}
                        sx={{
                            flex: `0 0 ${items.length > 1 ? ITEM_WIDTH : SINGLE_ITEM_WIDTH}`,
                            scrollSnapAlign: 'start',
                        }}
                    >
                        {item}
                    </Box>
                ))}
            </Box>

            {items.length > 1 && (
                <Stack direction='row' spacing={1} justifyContent='center'>
                    {items.map((_item, index) => (
                        <Box
                            // biome-ignore lint/suspicious/noArrayIndexKey: dots track slide position
                            key={index}
                            role='button'
                            aria-label={`${index + 1}`}
                            onClick={() => scrollTo(index)}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                backgroundColor: (theme) =>
                                    index === active ? theme.palette.primary.main : theme.palette.divider,
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
}

export default CardCarousel;
